import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import FlagIcon from "@mui/icons-material/Flag";
import FunctionsIcon from "@mui/icons-material/Functions";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PlayCircleFilledIcon from "@mui/icons-material/PlayCircleFilled";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import UnfoldLessIcon from "@mui/icons-material/UnfoldLess";
import UnfoldMoreIcon from "@mui/icons-material/UnfoldMore";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { useCallback, useMemo, useState, type FunctionComponent } from "react";
import type FeatureNodeType from "../features/FeatureNodeType";
import type NodeWalkthroughState from "../features/NodeWalkthroughState";
import type OperationNodeData from "../features/OperationNodeData";
import { monoFontFamily } from "../theme";
import EditableName from "./EditableName";
import "./NodeTitle.css";

interface NodeTitleProps {
  id: string;
  name: string;
  operationData: OperationNodeData | null;
  backgroundColor: string;
  isHighlighted: boolean;
  // Optional so that the existing callers don't have to pass them
  borderRadius?: string | number;
  borderBottomColor?: string;
  accentColor?: string;
  featureNodeType?: FeatureNodeType;
  walkthroughState?: NodeWalkthroughState;
  isCollapsed?: boolean;
  // The value to keep in sight while collapsed, null when there's none
  collapsedValue?: string | null;
  topologicalOrder?: number | null;
  isTopologicalOrderVisible?: boolean;
  onNameChange: (name: string) => void;
  onToggleCollapse?: () => void;
}

const NodeTitle: FunctionComponent<NodeTitleProps> = ({
  id,
  name,
  operationData,
  backgroundColor,
  isHighlighted,
  borderRadius = 0,
  borderBottomColor,
  accentColor,
  featureNodeType,
  walkthroughState,
  isCollapsed = false,
  collapsedValue = null,
  topologicalOrder = null,
  isTopologicalOrderVisible = false,
  onNameChange,
  onToggleCollapse,
}) => {
  const [isEditingName, setIsEditingName] = useState(false);

  const getClassName = useCallback(() => {
    return isEditingName ? "" : "drag-handle";
  }, [isEditingName]);

  const handleEditingChange = useCallback((isEditing: boolean) => {
    setIsEditingName(isEditing);
  }, []);

  // Tells the node type apart at a glance without reading the colors, which
  // matters when the graph is zoomed out
  const typeIcon = useMemo(() => {
    switch (featureNodeType?.nodeType) {
      case "constant":
        return {
          icon: <LockOutlinedIcon fontSize="small" />,
          title: "Constant",
        };
      case "variable":
        return {
          icon: <TuneOutlinedIcon fontSize="small" />,
          title: "Variable",
        };
      case "operation":
        return {
          icon: <FunctionsIcon fontSize="small" />,
          title: operationData === null ? "Operation" : operationData.name,
        };
      default:
        return null;
    }
  }, [featureNodeType?.nodeType, operationData]);

  const stepIcon = useMemo(() => {
    switch (walkthroughState?.status) {
      case "active":
        return {
          icon: <PlayCircleFilledIcon fontSize="small" />,
          title: "Evaluating this node",
        };
      case "done":
        return {
          icon: <CheckCircleIcon fontSize="small" />,
          title: "Already evaluated",
        };
      default:
        return null;
    }
  }, [walkthroughState?.status]);

  return (
    <Box
      data-testid={`node-title-${id}`}
      // corresponds to dragHandle when creating new reactflow.Node
      className={getClassName()}
      display="flex"
      sx={{
        backgroundColor,
        borderRadius,
        borderBottom: borderBottomColor === undefined ? undefined : 1,
        borderBottomColor,
      }}
      px={0.75}
      py={0.5}
    >
      <Stack direction="row" alignItems="center" flexGrow={1} spacing={0.5}>
        {/* Drag indicator */}
        <DragIndicatorIcon
          fontSize="small"
          sx={{ color: "text.disabled", fontSize: 16 }}
        />

        {/* Topological sort order badge */}
        {isTopologicalOrderVisible &&
          topologicalOrder !== undefined &&
          topologicalOrder !== null && (
            <Tooltip
              title={`Topological order: ${topologicalOrder}`}
              placement="top"
            >
              <Typography
                data-testid={`topological-order-${id}`}
                aria-label={`Topological order: ${topologicalOrder}`}
                variant="caption"
                sx={{
                  fontFamily: monoFontFamily,
                  fontWeight: 700,
                  fontSize: 10,
                  px: 0.5,
                  py: 0.1,
                  borderRadius: 0.5,
                  backgroundColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(244, 67, 54, 0.2)"
                      : "rgba(244, 67, 54, 0.1)",
                  color: (theme) =>
                    theme.palette.mode === "dark" ? "#ff6b6b" : "#d32f2f",
                  border: "1px solid",
                  borderColor: (theme) =>
                    theme.palette.mode === "dark"
                      ? "rgba(255, 107, 107, 0.4)"
                      : "rgba(211, 47, 47, 0.3)",
                  lineHeight: 1.2,
                  display: "inline-block",
                  userSelect: "none",
                }}
              >
                #{topologicalOrder}
              </Typography>
            </Tooltip>
          )}

        {/* Node type indicator */}
        {typeIcon !== null && (
          <Tooltip title={typeIcon.title} placement="top">
            <Box
              data-testid={`node-type-${id}`}
              aria-label={typeIcon.title}
              display="flex"
              sx={{ color: accentColor, "& svg": { fontSize: 16 } }}
            >
              {typeIcon.icon}
            </Box>
          </Tooltip>
        )}

        {/* Editable name */}
        <Box flexGrow={1} minWidth={0}>
          <EditableName
            name={name}
            operationData={operationData}
            onEditingChange={handleEditingChange}
            onNameChange={onNameChange}
          />
        </Box>

        {/* Value kept in sight while collapsed, so that a shrunk node still
        reports its result */}
        {isCollapsed && collapsedValue !== null && (
          <Typography
            data-testid={`collapsed-value-${id}`}
            variant="body2"
            sx={{
              fontFamily: monoFontFamily,
              fontWeight: 600,
              px: 0.75,
              borderRadius: 0.5,
              backgroundColor: "action.hover",
              // A long value must not stretch the node out of shape
              maxWidth: 96,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {collapsedValue}
          </Typography>
        )}

        {/* Derivative target indicator */}
        {isHighlighted && (
          <Tooltip title="Derivative target" placement="top">
            <Box
              data-testid={`derivative-target-${id}`}
              aria-label="Derivative target"
              display="flex"
              sx={{ color: "primary.main", "& svg": { fontSize: 16 } }}
            >
              <FlagIcon fontSize="small" />
            </Box>
          </Tooltip>
        )}

        {/* Step-by-step execution indicator */}
        {stepIcon !== null && (
          <Tooltip title={stepIcon.title} placement="top">
            <Box
              data-testid={`step-state-${id}`}
              aria-label={stepIcon.title}
              display="flex"
              sx={{ "& svg": { fontSize: 16 } }}
            >
              {stepIcon.icon}
            </Box>
          </Tooltip>
        )}

        {/* Collapse toggle */}
        {onToggleCollapse !== undefined && (
          <Tooltip
            title={isCollapsed ? "Expand node" : "Collapse node"}
            placement="top"
          >
            <IconButton
              data-testid={`collapse-toggle-${id}`}
              aria-label={isCollapsed ? "expand" : "collapse"}
              size="small"
              // The header is the drag handle, so a press here would otherwise
              // start dragging the node instead of hitting the button
              onMouseDown={(event) => {
                event.stopPropagation();
              }}
              onClick={onToggleCollapse}
            >
              {isCollapsed ? (
                <UnfoldMoreIcon fontSize="small" />
              ) : (
                <UnfoldLessIcon fontSize="small" />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
};

export default NodeTitle;
